import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { startOfDay, addYears } from "date-fns";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import truncate from "lodash/truncate";
import { useInvestmentsId } from "@bn/api/apiv1";
import ListIcon from "@icons/myicons/list.svg?react";
import ChartIcon from "@icons/myicons/chart.svg?react";
import DollarIcon from "@icons/myicons/dollar.svg?react";
import {
  AccountsHeader,
  CardHeading,
  CardPageLayout,
  ComparisonValue,
  GainLossPercents,
  InvestmentChart,
  PrimaryActionWithMenu,
  Tab,
  Tabs,
} from "@components";
import { SecurityImage } from "@components/SecurityImage";
import { getValueByInvestmentAndBookingType } from "@helpers";
import { useSecurityImages } from "@hooks";
import { useAccount } from "@api/v1/hooks/useAccount";
import { FundamentalsPageByInvestmentId } from "../../fundamentals";
import { InvestmentActionMenuItems } from "./components/InvestmentTable/InvestmentActionMenu";
import { EditInvestmentModal } from "./edit/EditInvestmentModal";
import { DeleteInvestmentDialog } from "./components/InvestmentTable/DeleteInvestmentDialog";
import { InvestmentIsinBanner } from "./components/InvestmentIsinBanner";
import { InvestmentProtocolContainer } from "./components/InvestmentProtocolContainer";
import { DividendsTableContainer } from "./components/DividendsTableContainer";
import { PerformanceOverview } from "./components/PerformanceOverview";
import { useChartData } from "./hooks/useChartData";
import { useInvestmentPerformanceOverview } from "./hooks/useInvestmentPerformanceOverview";

const tabKeys = {
  fundamentals: "fundamentals",
  protocol: "protocol",
  dividends: "dividends",
};

const availableIntervalTypes: IntervalType[] = [
  "oneDay",
  "oneWeek",
  "oneMonth",
  "YTD",
  "oneYear",
  "threeYears",
  "fiveYears",
  "sinceBeginning",
  "custom",
];
const availableCompactIntervalTypes: IntervalType[] = [
  "oneDay",
  "oneWeek",
  "oneMonth",
  "oneYear",
  "fiveYears",
  "custom",
];

const InvestmentChartStyled = styled(InvestmentChart)(({ theme }) => ({
  minHeight: 546,

  [theme.breakpoints.down("lg")]: {
    minHeight: 427,
  },

  [theme.breakpoints.down("sm")]: {
    boxShadow: "none",
  },
}));

const ChartTitle = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginBottom: theme.spacing(5),

  [theme.breakpoints.down("md")]: {
    marginBottom: 0,
  },
}));

export interface Props {
  investmentId: number;
  depotId?: number;
}

export const InvestmentPage = ({ investmentId, depotId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: account } = useAccount(depotId);
  const [searchParams, setSearchParams] = useSearchParams();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const { data: investment, isPending: isInvestmentPending } = useInvestmentsId(
    { id: investmentId }
  );

  const images = useSecurityImages(investment ? [investment] : undefined);
  const image =
    images[investment?.isin || ""] || images[investment?.tickerSymbol || ""];

  const [editModalShow, setEditModalShow] = useState(false);
  const [editModalTab, setEditModalTab] = useState<string>();
  const [deleteModalShow, setDeleteModalShow] = useState(false);

  const [currentInterval, setCurrentInterval] =
    useState<IntervalType>("oneYear");
  const [chartStartDate, setChartStartDate] = useState<Date | undefined>(
    startOfDay(addYears(new Date(), -1))
  );
  const [chartEndDate, setChartEndDate] = useState<Date | undefined>(
    new Date()
  );
  const [comparedTicker, setComparedTicker] = useState<ComparisonValue>();

  const { data: chartData = [], isFetching } = useChartData(
    {
      investmentId,
      currentInterval,
      tickerSymbol: investment?.tickerSymbol,
      tickerSymbolWithCurrency: investment?.tickerSymbolWithCurrency,
      quoteProvider: investment?.quoteProvider,
      startDate: chartStartDate,
      endDate: chartEndDate,
      comparedTickerSymbol: comparedTicker?.value,
      currency: investment?.currency,
    },
    { showEvents: true }
  );

  const {
    netGain,
    returnOnInvestment,
    returnOnInvestmentInPercent,
    performance,
    gainAndLoss,
  } = useInvestmentPerformanceOverview({ investmentId, investment });

  const quoteValue = investment?.quote?.last ?? 0;
  const chartHeaderValue =
    getValueByInvestmentAndBookingType({
      value: quoteValue,
      investmentType: investment?.type ?? "11_stock",
      currency: investment?.currency,
      percentageRange: "0-100",
      quoteNotation: investment?.quoteNotation,
    }) ?? 0;

  // Where to fall back to once this investment no longer exists.
  const parentPath = depotId ? `/depots/${depotId}` : "/investments";

  const breadcrumbs: BreadcrumbItem[] = [
    {
      to: "/",
      text: t("breadcrumbs.home"),
    },
    depotId
      ? {
          to: "/depots-accounts",
          text: t("breadcrumbs.depotsAccounts"),
        }
      : {
          to: "/investments",
          text: t("breadcrumbs.investments"),
        },
    ...(account
      ? [
          {
            to: parentPath,
            text: truncate(account?.name),
          },
        ]
      : []),
    {
      text:
        (investment?.hasValidIsin
          ? investment?.isin
          : truncate(investment?.name)) ?? "",
    },
  ];

  // Browser back restores the previous route with the scroll position of this
  // one, so the next page opens part-way down.
  useEffect(() => {
    const contentEl = document.getElementById("content");
    if (!contentEl) return;

    const scrollToTop = () => contentEl.scrollTo(0, 0);
    window.addEventListener("popstate", scrollToTop, true);
    return () => window.removeEventListener("popstate", scrollToTop, true);
  }, []);

  const handleRangeSelect = (type: IntervalType, value?: RangeType) => {
    setCurrentInterval(type);
    setChartStartDate(value?.startDate);
    setChartEndDate(value?.endDate);

    if (type === "oneDay") {
      setComparedTicker(undefined);
    }
  };

  const handleOpenEditModal = (tab?: string) => {
    setEditModalTab(tab);
    setEditModalShow(true);
  };

  return (
    <CardPageLayout
      data-testid="investment-page"
      breadcrumbs={breadcrumbs}
      cardHeading={
        <CardHeading
          actionsInline
          title={
            <AccountsHeader
              title={
                investment?.standardisedName ||
                investment?.name ||
                investment?.isin
              }
              subtitle={
                [investment?.isin, investment?.wkn]
                  .filter(Boolean)
                  .join(" · ") || undefined
              }
              icon={<SecurityImage src={image} alt={investment?.name} />}
            />
          }
          actions={
            <PrimaryActionWithMenu
              primaryActionText={t(
                "investment.header.actions.addBookingButton"
              )}
              primaryActionUrl={`/add-transaction-wizard/${investmentId}`}
            >
              <InvestmentActionMenuItems
                onAddBooking={() =>
                  navigate(`/add-transaction-wizard/${investmentId}`)
                }
                onEdit={() => handleOpenEditModal()}
                onDelete={() => setDeleteModalShow(true)}
              />
            </PrimaryActionWithMenu>
          }
        />
      }
      cardChildren={
        <InvestmentChartStyled
          showEvents
          drawLines={!isSm}
          isFetching={isFetching}
          data={chartData}
          availableDateRanges={availableIntervalTypes}
          availableCompactDateRanges={availableCompactIntervalTypes}
          title={
            <ChartTitle>
              <Typography variant="body2">
                {t("investment.header.chart.subheader")}
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h2">{chartHeaderValue}</Typography>
                <GainLossPercents
                  value={investment?.quote?.changePercentAbs ?? 0}
                />
              </Box>
            </ChartTitle>
          }
          comparedTicker={comparedTicker}
          onComparedTickerChange={setComparedTicker}
          onSelect={handleRangeSelect}
          bottomBarComponent={
            <PerformanceOverview
              showPerformanceReview={!isInvestmentPending}
              currency={investment?.currency ?? "EUR"}
              gainAndLoss={gainAndLoss}
              realizedGains={netGain}
              returnOnInvestment={returnOnInvestment}
              returnOnInvestmentInPercent={returnOnInvestmentInPercent}
              performance={performance}
            />
          }
        />
      }
    >
      <InvestmentIsinBanner
        investment={investment}
        onEditIsin={() => handleOpenEditModal("advanced")}
      />

      <Tabs
        onChange={(key) => setSearchParams({ tab: key })}
        defaultKey={searchParams.get("tab")}
      >
        <Tab
          label={isMd ? <ChartIcon /> : t("investment.tabs.fundamentals")}
          key={tabKeys.fundamentals}
        >
          <FundamentalsPageByInvestmentId investmentId={investmentId} />
        </Tab>
        {investment?.quote?.last != null && (
          <Tab
            label={isMd ? <ListIcon /> : t("investment.tabs.protocol")}
            key={tabKeys.protocol}
          >
            <InvestmentProtocolContainer
              investmentId={investmentId}
              investmentQuote={quoteValue}
              currencySymbol={investment.currency}
              currentMarketValue={investment.currentMarketValue ?? 0}
              image={image}
            />
          </Tab>
        )}
        <Tab
          label={isMd ? <DollarIcon /> : t("investment.tabs.dividends")}
          key={tabKeys.dividends}
        >
          <DividendsTableContainer
            investmentId={investmentId}
            currency={investment?.currency}
            image={image}
          />
        </Tab>
      </Tabs>

      <EditInvestmentModal
        investment={investment}
        image={image}
        isOpen={editModalShow}
        defaultTab={editModalTab}
        onClose={() => setEditModalShow(false)}
      />

      <DeleteInvestmentDialog
        investmentId={investmentId}
        isOpen={deleteModalShow}
        onClose={() => setDeleteModalShow(false)}
        onSuccess={() => navigate(parentPath)}
      />
    </CardPageLayout>
  );
};
